import { getCookie, getSelectedRestaurantId } from './requestMetadata'

export type SseEventHandler = (
  eventType: string,
  data: string,
) => unknown

export interface ConsumeSseResult {
  /** Terminal event accepted by the handler, or null when the stream reached EOF. */
  terminalEvent: string | null
}

export interface ConsumeSseOptions {
  /**
   * Event names after which the reader should be cancelled. The handler may
   * return false to reject a malformed terminal event; rejection throws rather
   * than reporting a successful terminal event.
   */
  stopOnEvents?: readonly string[]
  /** Event name used when a frame contains data without an `event:` field. */
  defaultEvent?: string
  /** Optional request signal used to stop parsing between chunks. */
  signal?: AbortSignal
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function parseSseJson(data: string): unknown {
  try {
    return JSON.parse(data) as unknown
  } catch {
    return undefined
  }
}

/**
 * Fetch an authenticated API request with the same browser metadata as the
 * Axios client. Header values are read when the request starts so a newly
 * selected restaurant or refreshed token is used by every stream.
 */
export function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(input instanceof Request ? input.headers : undefined)
  new Headers(init.headers).forEach((value, name) => headers.set(name, value))

  try {
    if (typeof localStorage !== 'undefined') {
      const authToken = localStorage.getItem('authToken')
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`)
      }
    }
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts. Cookie
    // authentication still allows the request to proceed.
  }

  const method = (init.method ?? (input instanceof Request ? input.method : 'GET')).toLowerCase()
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrfToken = getCookie('PLAY_CSRF_TOKEN')
    if (csrfToken) {
      headers.set('Csrf-Token', csrfToken)
    }
  }

  const selectedRestaurantId = getSelectedRestaurantId()
  if (selectedRestaurantId !== null) {
    headers.set('X-Selected-Restaurant-Id', String(selectedRestaurantId))
  }

  return fetch(input, {
    ...init,
    credentials: 'include',
    headers,
  })
}

/**
 * Consume a fetch response formatted as Server-Sent Events.
 *
 * The parser deliberately leaves JSON decoding to the caller: different
 * streams use different event payloads, while framing rules are shared. It
 * handles chunk boundaries, CRLF, multiline data fields, comments, implicit
 * `message` events, and a final frame without a blank-line terminator.
 */
export async function consumeSse(
  response: Response,
  onEvent: SseEventHandler,
  options: ConsumeSseOptions = {},
): Promise<ConsumeSseResult> {
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  const decoder = new TextDecoder()
  const stopOnEvents = new Set(options.stopOnEvents ?? [])
  const defaultEvent = options.defaultEvent ?? 'message'
  let buffer = ''
  let eventType: string | null = null
  let dataLines: string[] = []
  let terminalEvent: string | null = null

  const abortError = (): Error => {
    const error = new Error('The operation was aborted')
    error.name = 'AbortError'
    return error
  }

  const handleAbort = () => {
    void reader.cancel(abortError()).catch(() => {
      // The request body may already have failed as part of fetch cancellation.
    })
  }
  options.signal?.addEventListener('abort', handleAbort, { once: true })

  const dispatch = async (): Promise<boolean> => {
    const hasData = dataLines.length > 0
    const data = dataLines.join('\n')
    const type = eventType ?? defaultEvent

    eventType = null
    dataLines = []

    if (!hasData) {
      return false
    }

    const accepted = (await onEvent(type, data)) !== false
    if (stopOnEvents.has(type)) {
      if (!accepted) {
        throw new Error(`Rejected terminal SSE event: ${type}`)
      }
      terminalEvent = type
      await reader.cancel()
      return true
    }
    return false
  }

  const processLine = async (rawLine: string): Promise<boolean> => {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine

    if (line === '') {
      return dispatch()
    }

    // SSE comments are commonly used as heartbeat frames.
    if (line.startsWith(':')) {
      return false
    }

    const separator = line.indexOf(':')
    const field = separator === -1 ? line : line.slice(0, separator)
    let value = separator === -1 ? '' : line.slice(separator + 1)
    if (value.startsWith(' ')) {
      value = value.slice(1)
    }

    switch (field) {
      case 'event':
        eventType = value
        break
      case 'data':
        dataLines.push(value)
        break
      default:
        // Ignore `id`, `retry`, and extension fields. They do not affect the
        // payload delivered to the stream-specific handler.
        break
    }
    return false
  }

  const processText = async (text: string): Promise<boolean> => {
    buffer += text
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      if (await processLine(line)) {
        return true
      }
    }
    return false
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an SSE reader may remain open indefinitely
    while (true) {
      if (options.signal?.aborted) {
        throw abortError()
      }

      const result = await reader.read()
      if (options.signal?.aborted) {
        throw abortError()
      }
      if (result.done) {
        break
      }

      if (await processText(decoder.decode(result.value, { stream: true }))) {
        return { terminalEvent }
      }
    }

    if (await processText(decoder.decode())) {
      return { terminalEvent }
    }

    // A stream may close immediately after a data line without sending the
    // blank line that normally dispatches the frame.
    if (buffer !== '') {
      const trailingLine = buffer
      buffer = ''
      if (await processLine(trailingLine)) {
        return { terminalEvent }
      }
    }

    await dispatch()
    return { terminalEvent }
  } catch (error) {
    try {
      await reader.cancel(error)
    } catch {
      // Ignore a secondary cancellation failure and preserve the root error.
    }
    throw error
  } finally {
    // A caller can abort the fetch while a read is pending. Releasing the
    // reader lock keeps the response usable by the browser and is harmless
    // after an explicit terminal-event cancellation.
    options.signal?.removeEventListener('abort', handleAbort)
    reader.releaseLock()
  }
}
